import { useEffect, useState, useCallback, useMemo } from "react";
import Layout from "../components/Layout";
import { http, formatApiError, API } from "../lib/api";
import { toast } from "sonner";
import SearchBar, { matchesSearch } from "../components/SearchBar";
import {
  Receipt, CheckCircle, ArrowCounterClockwise, DownloadSimple, CurrencyInr,
  Buildings, Clock, User,
} from "@phosphor-icons/react";

const TABS = [
  { key: "all", label: "All" },
  { key: "unpaid", label: "Unpaid" },
  { key: "paid", label: "Paid" },
];

function money(n) {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })
    .format(n || 0);
}

export default function InvoicesPage() {
  const [tab, setTab] = useState("all");
  const [data, setData] = useState({ rows: [], totals: { count: 0, billed: 0, paid: 0, outstanding: 0 } });
  const [projectMeta, setProjectMeta] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = tab === "all" ? "/invoices" : `/invoices?status=${tab}`;
      const [invRes, projRes] = await Promise.all([
        http.get(url),
        http.get("/projects").catch(() => ({ data: [] })),
      ]);
      setData(invRes.data);
      setProjectMeta(projRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (taskId) => {
    try { await http.patch(`/invoices/${taskId}/mark-paid`); toast.success("Marked paid"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const markUnpaid = async (taskId) => {
    try { await http.patch(`/invoices/${taskId}/mark-unpaid`); toast.success("Reverted to unpaid"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const clientByProject = useMemo(() => {
    const m = {};
    (projectMeta || []).forEach((p) => { m[p.name] = p.client_name || ""; });
    return m;
  }, [projectMeta]);

  const filteredRows = useMemo(() => {
    return (data.rows || []).filter((r) =>
      matchesSearch(search, r.project, clientByProject[r.project])
    );
  }, [data.rows, search, clientByProject]);

  const groupedByProject = useMemo(() => {
    const map = {};
    filteredRows.forEach((r) => {
      const k = r.project || "—";
      if (!map[k]) map[k] = [];
      map[k].push(r);
    });
    return Object.entries(map);
  }, [filteredRows]);

  return (
    <Layout
      title="Invoices"
      subtitle="Client-approved milestones become invoices. Mark them paid as payments come in."
    >
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-300 bg-white mb-6" data-testid="invoice-totals">
        <SummaryCell label="Invoices" value={data.totals.count} icon={Receipt} />
        <SummaryCell label="Total Billed" value={money(data.totals.billed)} icon={CurrencyInr} />
        <SummaryCell label="Collected" value={money(data.totals.paid)} icon={CheckCircle} accent="text-emerald-700" />
        <SummaryCell label="Outstanding" value={money(data.totals.outstanding)} icon={Clock} accent="text-orange-700" />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6" data-testid="invoice-toolbar">
        <div className="flex items-center gap-0 border border-zinc-300 bg-white w-fit" data-testid="invoice-tabs">
          {TABS.map((t) => (
            <button
              key={t.key} data-testid={`inv-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 sm:px-5 py-2.5 label-overline border-r border-zinc-300 last:border-r-0 ${
                tab === t.key ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"
              }`}
            >{t.label}</button>
          ))}
        </div>
        <SearchBar
          value={search}
          onChange={setSearch}
          testId="invoices-search-input"
          placeholder="Search by Job No. or Client Name…"
          className="sm:ml-auto"
        />
      </div>

      {loading && <div className="label-overline text-zinc-500">Loading…</div>}

      {!loading && filteredRows.length === 0 && data.rows.length > 0 && (
        <div className="border border-dashed border-zinc-300 p-10 text-center bg-white" data-testid="invoices-search-empty">
          <div className="label-overline text-zinc-500">No invoices match &quot;{search}&quot;</div>
        </div>
      )}

      {!loading && data.rows.length === 0 && (
        <div className="border border-dashed border-zinc-300 p-16 text-center bg-white">
          <Receipt size={36} weight="bold" className="mx-auto text-zinc-300" />
          <div className="label-overline text-zinc-500 mt-3">No invoices yet</div>
          <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
            When a client approves a completed milestone (from their portal or share link),
            an invoice will appear here automatically.
          </p>
        </div>
      )}

      {!loading && groupedByProject.map(([proj, rows]) => (
        <div key={proj} className="mb-6 border border-zinc-300 bg-white">
          <div className="px-5 py-3 border-b border-zinc-300 bg-zinc-50 flex items-center gap-3">
            <Buildings size={14} weight="bold" className="text-blue-600" />
            <span className="label-overline text-zinc-900">{proj}</span>
            <span className="ml-auto label-overline bg-zinc-900 text-white px-2 py-0.5">
              {rows.length} · {money(rows.reduce((s, r) => s + (r.amount || 0), 0))}
            </span>
          </div>
          <div>
            {rows.map((r, i) => (
              <InvoiceRow
                key={r.task_id} r={r}
                divider={i < rows.length - 1}
                onMarkPaid={markPaid} onMarkUnpaid={markUnpaid}
              />
            ))}
          </div>
        </div>
      ))}
    </Layout>
  );
}

function InvoiceRow({ r, divider, onMarkPaid, onMarkUnpaid }) {
  const paid = r.status === "paid";
  const invoiceUrl = `${API}/invoices/${r.task_id}/html`;
  const pdfUrl = `${API}/invoices/${r.task_id}/pdf`;
  return (
    <div data-testid={`invoice-row-${r.task_id}`}
         className={`p-4 flex items-start gap-4 ${divider ? "border-b border-zinc-200" : ""} ${paid ? "bg-emerald-50/40" : ""}`}>
      <div className={`shrink-0 w-10 h-10 border flex items-center justify-center ${
        paid ? "border-emerald-500 text-emerald-700" : "border-orange-500 text-orange-600"
      }`}>
        <Receipt size={18} weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display font-bold text-zinc-900">{r.milestone_title}</span>
          <span className="text-xs font-mono text-zinc-500">№ {r.invoice_number}</span>
          <span className={`label-overline ml-2 px-2 py-0.5 ${
            paid ? "bg-emerald-600 text-white" : "bg-orange-100 text-orange-800"
          }`}>{r.status}</span>
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500 font-mono flex-wrap">
          <span className="flex items-center gap-1">
            <User size={11} weight="bold" /> Approved by {r.approved_by || "—"}
          </span>
          {r.approved_at && (
            <span>· {new Date(r.approved_at).toLocaleDateString()}</span>
          )}
          {r.paid_at && (
            <span className="text-emerald-700">· Paid {new Date(r.paid_at).toLocaleDateString()}</span>
          )}
        </div>
        {r.note && (
          <div className="mt-2 text-xs text-zinc-600 italic">&quot;{r.note}&quot;</div>
        )}
      </div>
      <div className="shrink-0 text-right">
        <div className="font-display text-xl font-black tracking-tighter text-zinc-900">{money(r.amount)}</div>
        <div className="mt-2 flex items-center gap-1.5 justify-end">
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
             data-testid={`open-invoice-${r.task_id}`}
             className="inline-flex items-center gap-1 label-overline border border-zinc-400 bg-white text-zinc-700 hover:bg-zinc-900 hover:text-white px-2.5 py-1.5">
            <DownloadSimple size={11} weight="bold" /> PDF
          </a>
          {paid ? (
            <button onClick={() => onMarkUnpaid(r.task_id)}
                    data-testid={`unpaid-${r.task_id}`}
                    className="inline-flex items-center gap-1 label-overline border border-zinc-400 bg-white text-zinc-700 hover:bg-zinc-100 px-2.5 py-1.5">
              <ArrowCounterClockwise size={11} weight="bold" /> Unpaid
            </button>
          ) : (
            <button onClick={() => onMarkPaid(r.task_id)}
                    data-testid={`paid-${r.task_id}`}
                    className="inline-flex items-center gap-1 label-overline border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 px-2.5 py-1.5">
              <CheckCircle size={11} weight="bold" /> Paid
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCell({ label, value, icon: Icon, accent = "text-zinc-900" }) {
  return (
    <div className="border-r border-zinc-300 last:border-r-0 p-5 flex items-center justify-between">
      <div>
        <div className="label-overline text-zinc-500">{label}</div>
        <div className={`font-display text-2xl font-black tracking-tighter mt-1 ${accent}`}>{value}</div>
      </div>
      <Icon size={22} weight="bold" className={accent} />
    </div>
  );
}
